import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Box, Typography, Grid, Paper, Button, Chip, Alert,
  CircularProgress, Divider, RadioGroup, FormControlLabel,
  Radio, FormControl, FormLabel, Select, MenuItem, InputLabel,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  CreditCard as CreditCardIcon,
  Upgrade as UpgradeIcon,
} from '@mui/icons-material';
import api from '../../services/api';

const STATUS_COLOR = {
  ATIVO: 'success', TRIAL: 'info', SUSPENSO: 'warning',
  CANCELADO: 'error', EXPIRADO: 'error', PENDENTE_PAGAMENTO: 'warning',
};

const AtribuirPlano = ({ instituicaoId }) => {
  const [planos, setPlanos] = useState([]);
  const [assinatura, setAssinatura] = useState(null);
  const [loadingPlanos, setLoadingPlanos] = useState(true);
  const [loadingAssinatura, setLoadingAssinatura] = useState(true);
  const [planoSelecionado, setPlanoSelecionado] = useState('');
  const [tipoCobranca, setTipoCobranca] = useState('MENSAL');
  const [salvando, setSalvando] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const carregarDados = useCallback(async () => {
    try {
      const { data } = await api.get('/instituicoes/admin/planos-disponiveis');
      setPlanos(Array.isArray(data) ? data : []);
    } catch { setPlanos([]); }
    finally { setLoadingPlanos(false); }

    try {
      const { data } = await api.get(`/billing/assinatura/${instituicaoId}`);
      if (data.sucesso) setAssinatura(data.assinatura);
    } catch { setAssinatura(null); }
    finally { setLoadingAssinatura(false); }
  }, [instituicaoId]);

  useEffect(() => { carregarDados(); }, [carregarDados]);

  const handleAtribuir = async () => {
    if (!planoSelecionado) return;
    setSalvando(true);
    setFeedback(null);
    try {
      await api.post('/billing/criar-assinatura', {
        instituicao_id: parseInt(instituicaoId),
        plano_id: parseInt(planoSelecionado),
        tipo_cobranca: tipoCobranca,
        iniciar_trial: false,
      });
      setFeedback({ type: 'success', msg: 'Plano atribuído com sucesso! Os módulos foram activados automaticamente.' });
      await carregarDados();
    } catch (err) {
      setFeedback({ type: 'error', msg: err.response?.data?.message || err.response?.data?.erro || 'Erro ao atribuir plano.' });
    } finally {
      setSalvando(false);
    }
  };

  const handleAlterar = async () => {
    if (!planoSelecionado || !assinatura?.id) return;
    setSalvando(true);
    setFeedback(null);
    try {
      await api.put(`/billing/alterar-plano/${assinatura.id}`, {
        novo_plano_id: parseInt(planoSelecionado),
        tipo_cobranca: tipoCobranca,
      });
      setFeedback({ type: 'success', msg: 'Plano alterado com sucesso! Os módulos foram actualizados.' });
      await carregarDados();
    } catch (err) {
      setFeedback({ type: 'error', msg: err.response?.data?.message || err.response?.data?.erro || 'Erro ao alterar plano.' });
    } finally {
      setSalvando(false);
    }
  };

  const temAssinaturaActiva = assinatura && ['ATIVO', 'TRIAL'].includes(assinatura.status);

  if (loadingPlanos || loadingAssinatura) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>Plano de Subscrição</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Atribua ou altere o plano desta instituição. Os módulos são activados automaticamente.
      </Typography>

      {feedback && (
        <Alert severity={feedback.type} sx={{ mb: 2 }} onClose={() => setFeedback(null)}>
          {feedback.msg}
        </Alert>
      )}

      {/* Assinatura actual */}
      {assinatura && (
        <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <CreditCardIcon color="primary" fontSize="small" />
            <Typography variant="subtitle2" fontWeight={600}>Plano Actual</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <Typography variant="body1" fontWeight={600}>{assinatura.plano_nome || assinatura.plano?.nome}</Typography>
            <Chip
              label={assinatura.status}
              color={STATUS_COLOR[assinatura.status] || 'default'}
              size="small"
            />
            <Typography variant="caption" color="text.secondary">
              {assinatura.tipo_cobranca} · Vence em {assinatura.data_vencimento ? new Date(assinatura.data_vencimento).toLocaleDateString('pt-MZ') : '—'}
            </Typography>
          </Box>
        </Paper>
      )}

      {/* Selecção de plano */}
      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }} color="primary">
          {temAssinaturaActiva ? 'Alterar Plano' : 'Seleccionar Plano'}
        </Typography>

        <RadioGroup value={planoSelecionado} onChange={(e) => setPlanoSelecionado(e.target.value)}>
          <Grid container spacing={2}>
            {planos.map((plano) => {
              const modulos = Array.isArray(plano.modulos_incluidos)
                ? plano.modulos_incluidos
                : (typeof plano.modulos_incluidos === 'string' ? JSON.parse(plano.modulos_incluidos || '[]') : []);
              const selected = planoSelecionado === String(plano.id);
              return (
                <Grid item xs={12} sm={6} md={4} key={plano.id}>
                  <Paper
                    variant="outlined"
                    onClick={() => setPlanoSelecionado(String(plano.id))}
                    sx={{
                      p: 2, borderRadius: 2, cursor: 'pointer',
                      borderColor: selected ? 'primary.main' : 'divider',
                      bgcolor: selected ? 'primary.50' : 'background.paper',
                      transition: 'all 0.2s',
                      '&:hover': { borderColor: 'primary.light' },
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Typography variant="subtitle2" fontWeight={700}>{plano.nome}</Typography>
                      <Radio value={String(plano.id)} size="small" sx={{ p: 0 }} />
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      {plano.preco_mensal > 0
                        ? `${(plano.preco_mensal / 100).toLocaleString('pt-MZ')} MT/mês`
                        : 'Gratuito'}
                    </Typography>
                    {modulos.length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                        {modulos.slice(0, 4).map(m => (
                          <Chip key={m} label={m} size="small" sx={{ height: 16, fontSize: '0.6rem' }} icon={<CheckIcon sx={{ fontSize: '10px !important' }} />} />
                        ))}
                        {modulos.length > 4 && (
                          <Chip label={`+${modulos.length - 4}`} size="small" sx={{ height: 16, fontSize: '0.6rem' }} />
                        )}
                      </Box>
                    )}
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </RadioGroup>

        <Divider sx={{ my: 2 }} />

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Tipo de cobrança</InputLabel>
          <Select value={tipoCobranca} label="Tipo de cobrança" onChange={(e) => setTipoCobranca(e.target.value)}>
            <MenuItem value="MENSAL">Mensal</MenuItem>
            <MenuItem value="ANUAL">Anual</MenuItem>
            <MenuItem value="GRATUITO">Gratuito</MenuItem>
          </Select>
        </FormControl>

        <Box sx={{ mt: 2 }}>
          <Button
            variant="contained"
            startIcon={salvando ? <CircularProgress size={16} color="inherit" /> : <UpgradeIcon />}
            onClick={temAssinaturaActiva ? handleAlterar : handleAtribuir}
            disabled={!planoSelecionado || salvando}
          >
            {temAssinaturaActiva ? 'Alterar Plano' : 'Atribuir Plano'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

AtribuirPlano.propTypes = {
  instituicaoId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default AtribuirPlano;
